import { type ComponentType, useCallback, useEffect } from "react";
import { useRouter } from "solito/router";
import { match } from "ts-pattern";
import { isWeb } from "@medbillai/ui";
import { loginUrl } from "@medbillai/utils/urls";
import { analyticsService } from "../../lib/analytics";
import type { FlowState, FlowTransition } from "../../lib/flow-engine";
import { useSignUpOnboardingStore } from "../../lib/sign-up-onboarding/signUpOnboardingStore";
import { SingularEvents, singularService } from "../../lib/singular";
import { onboardingStorage } from "../../lib/storage/instances";
import { type AuthHandoff, ONBOARDING_VERSION, type OnboardingConfig, OnboardingFlow, type OnboardingTransitionProps, Screen } from "./OnboardingFlow";
const trackTransitionEvent = (event: FlowTransition<OnboardingConfig>) => {
  const eventName = event.outcome.tag === "back" ? "OnboardingFlowStepBackward" : "OnboardingFlowStepForward";
  analyticsService.trackEvent(eventName, {
    from: event.from.tag,
    to: event.to.tag,
    onboardingVersion: ONBOARDING_VERSION
  });
  if (event.from.tag === "IntentFork" && event.outcome.tag === "selected") {
    analyticsService.trackEvent("SignUpOnboardingUserFeaturesCompleted", {
      selections: [event.outcome.data.intent],
      onboardingVersion: ONBOARDING_VERSION
    });
    singularService.event(SingularEvents.onboardingQuestionAnswered, {
      questionId: "intent",
      selections: event.outcome.data.intent
    });
  }
};
const computeInitial = (phase: OnboardingPhase): FlowState<OnboardingConfig> => {
  if (phase === "preauth") {
    return isWeb ? Screen.IntentFork() : Screen.ValueProps();
  }
  return Screen.AccountCreated();
};
export type OnboardingPhase = "preauth" | "postauth";
export type OnboardingShellProps = {
  phase: OnboardingPhase;
  /** Per-platform screen-transition wrapper — see `OnboardingFlow`. */
  Transition: ComponentType<OnboardingTransitionProps>;
};

/**
 * Glue between the pure `OnboardingFlow` and the rest of the system: picks
 * initial state from platform, wires analytics, owns the stash lifecycle,
 * and performs redirects on terminal handoff.
 */
export function OnboardingShell({
  phase,
  Transition
}: OnboardingShellProps) {
  const {
    push,
    replace
  } = useRouter();
  const {
    setIsSignUpOnboarding
  } = useSignUpOnboardingStore();
  useEffect(() => {
    if (phase !== "preauth") {
      return;
    }
    analyticsService.trackEvent("SignUpOnboardingFlowStarted", {
      onboardingVersion: ONBOARDING_VERSION
    });
  }, [phase]);
  const handleAuthHandoff = useCallback((event: AuthHandoff) => {
    match(event).with({
      tag: "awaitingAccountCreation"
    }, ({
      data
    }) => {
      void onboardingStorage.set("intent", data.intent);
      setIsSignUpOnboarding(true);
      push(loginUrl({
        variant: data.intent
      }));
    }).with({
      tag: "exitToLogin"
    }, () => {
      push(loginUrl());
    }).exhaustive();
  }, [push, setIsSignUpOnboarding]);
  const handleComplete = useCallback(() => {
    void onboardingStorage.remove("intent");
    setIsSignUpOnboarding(false);
    replace("/");
  }, [replace, setIsSignUpOnboarding]);
  return <OnboardingFlow initial={computeInitial(phase)} ctx={{
    isWeb
  }} onAuthHandoff={handleAuthHandoff} onComplete={handleComplete} onTransition={trackTransitionEvent} Transition={Transition} data-sentry-element="OnboardingFlow" data-sentry-component="OnboardingShell" data-sentry-source-file="OnboardingShell.tsx" />;
}